import { DateTime } from 'luxon';
import { BookingStatus, StayRefundRule } from '../../../__generated__/graphql';
import type { BadgeTone } from '../../components/ui/Badge';
import type { IStayQuoteFieldsFragment } from '../../services/stayService/__generated__/queries.generated';

/** The booking fields these helpers read (a subset of `StayBookingFields`). */
export interface StayBookingLike {
  status: BookingStatus;
  startAt: number;
  endAt: number;
  requestExpiresAt?: string | Date | null;
}

export interface StayStatusView {
  label: string;
  tone: BadgeTone;
  /** One sentence under the label, from the viewer's side. */
  description: string;
}

type Viewer = 'guest' | 'host';

const HOUR_MS = 60 * 60 * 1000;

const CANCELLED = new Set<BookingStatus>([
  BookingStatus.CancelledByGuestMoreThan_24Hours,
  BookingStatus.CancelledByGuestLessThan_24HoursBeforeTripStart,
  BookingStatus.CancelledByGuestAfterTripStart,
  BookingStatus.CancelledByHost,
  BookingStatus.CancelledByAdmin,
]);

/** "23 hours left", "40 minutes left", or null once the time is up. */
export function timeLeftLabel(
  until: string | Date | null | undefined,
  now = Date.now(),
): string | null {
  if (!until) return null;
  const left = new Date(until).getTime() - now;
  if (!(left > 0)) return null;
  if (left >= HOUR_MS) {
    const hours = Math.floor(left / HOUR_MS);
    return `${hours} hour${hours === 1 ? '' : 's'} left`;
  }
  const minutes = Math.max(1, Math.floor(left / 60000));
  return `${minutes} minute${minutes === 1 ? '' : 's'} left`;
}

/** Status pill and sentence for a stay booking (spec 016 W18, W19). */
export function stayStatusView(
  booking: StayBookingLike,
  viewer: Viewer,
  now = Date.now(),
): StayStatusView {
  const left = timeLeftLabel(booking.requestExpiresAt, now);
  switch (booking.status) {
    case BookingStatus.Pending:
      return viewer === 'guest'
        ? {
            label: 'Not finished',
            tone: 'warning',
            description: 'Payment was not completed. Finish checkout to send it to the host.',
          }
        : {
            label: 'Checkout started',
            tone: 'neutral',
            description: 'The guest has not paid yet.',
          };
    case BookingStatus.Requested:
      return viewer === 'guest'
        ? {
            label: 'Request sent',
            tone: 'info',
            description: `Your card is on hold, not charged. The host has ${left ?? 'a little time'} to answer.`,
          }
        : {
            label: 'Needs your answer',
            tone: 'warning',
            description: `Accept or decline. ${left ? `${left[0].toUpperCase()}${left.slice(1)}.` : 'The request expires soon.'}`,
          };
    case BookingStatus.Booked:
      if (now >= booking.endAt) {
        return { label: 'Completed', tone: 'success', description: 'Checkout time has passed.' };
      }
      if (now >= booking.startAt) {
        return {
          label: viewer === 'guest' ? 'Current stay' : 'Hosting now',
          tone: 'success',
          description: 'The stay is in progress.',
        };
      }
      return {
        label: 'Confirmed',
        tone: 'success',
        description: viewer === 'guest' ? 'Your stay is booked.' : 'This stay is booked.',
      };
    case BookingStatus.Completed:
      return { label: 'Completed', tone: 'success', description: 'This stay is over.' };
    case BookingStatus.DeclinedByHost:
      return {
        label: 'Declined',
        tone: 'error',
        description:
          viewer === 'guest'
            ? 'The host could not host you. The hold on your card was released.'
            : 'You declined this request.',
      };
    case BookingStatus.RequestExpired:
      return {
        label: 'Expired',
        tone: 'neutral',
        description: 'The host did not answer in 24 hours. The card hold was released.',
      };
    case BookingStatus.RequestWithdrawn:
      return {
        label: 'Withdrawn',
        tone: 'neutral',
        description:
          viewer === 'guest' ? 'You withdrew this request.' : 'The guest withdrew this request.',
      };
    case BookingStatus.CardDeclined:
      return {
        label: 'Payment declined',
        tone: 'error',
        description:
          viewer === 'guest'
            ? 'Your card was declined. Book again with another card.'
            : 'The guest’s card was declined, so the stay was not booked.',
      };
    default:
      if (CANCELLED.has(booking.status)) {
        return {
          label: 'Cancelled',
          tone: 'error',
          description:
            booking.status === BookingStatus.CancelledByHost
              ? 'The host cancelled this stay.'
              : 'The guest cancelled this stay.',
        };
      }
      return { label: String(booking.status), tone: 'neutral', description: '' };
  }
}

export type StayTripGroup = 'requests' | 'upcoming' | 'past' | 'cancelled';

/** Which list tab a booking goes in (W21). Unpaid checkouts are left out. */
export function stayTripGroup(booking: StayBookingLike, now = Date.now()): StayTripGroup | null {
  switch (booking.status) {
    case BookingStatus.Pending:
      return null;
    case BookingStatus.Requested:
      return 'requests';
    case BookingStatus.Booked:
      return booking.endAt > now ? 'upcoming' : 'past';
    case BookingStatus.Completed:
    case BookingStatus.Shortened:
      return 'past';
    default:
      return 'cancelled';
  }
}

/** Sorts a tab: requests and upcoming soonest first, the rest newest first. */
export function sortForGroup<T extends StayBookingLike>(group: StayTripGroup, rows: T[]): T[] {
  const ascending = group === 'requests' || group === 'upcoming';
  return [...rows].sort((a, b) => (ascending ? a.startAt - b.startAt : b.startAt - a.startAt));
}

/** "Fri, Mar 20, 3:00 PM" in the listing's time zone. */
export function stayDateTime(ms: number, zone?: string | null): string {
  return DateTime.fromMillis(Number(ms), { zone: zone || 'UTC' }).toFormat('EEE, MMM d, h:mm a');
}

/** "Mar 20 to 23" or "Mar 30 to Apr 2", in the listing's time zone. */
export function stayDateRange(startAt: number, endAt: number, zone?: string | null): string {
  const start = DateTime.fromMillis(Number(startAt), { zone: zone || 'UTC' });
  const end = DateTime.fromMillis(Number(endAt), { zone: zone || 'UTC' });
  const endLabel = start.hasSame(end, 'month') ? end.toFormat('d') : end.toFormat('MMM d');
  const year = start.hasSame(DateTime.now(), 'year') ? '' : `, ${end.toFormat('yyyy')}`;
  return `${start.toFormat('MMM d')} to ${endLabel}${year}`;
}

export interface StayReceiptLike {
  totalPrice?: number | null;
  nights?: number | null;
  pricePerNight?: number | null;
  nightlySubtotal?: number | null;
  weeklyDiscountAmount?: number | null;
  monthlyDiscountAmount?: number | null;
  cleaningFee?: number | null;
  extraGuestFee?: number | null;
  petFee?: number | null;
  guestServiceFee?: number | null;
  guestServiceFeeRate?: number | null;
  occupancyTax?: number | null;
}

/**
 * The receipt of a stay booking in the quote's shape, so the booking page shows the
 * same lines the guest saw before paying (W20).
 */
export function receiptQuote(receipt: StayReceiptLike): IStayQuoteFieldsFragment {
  const n = (v?: number | null) => Number(v ?? 0) || 0;
  const nights = Math.max(1, n(receipt.nights));
  const subtotal = n(receipt.nightlySubtotal);
  return {
    nights,
    pricePerNight: n(receipt.pricePerNight) || subtotal / nights,
    nightlySubtotal: subtotal,
    weeklyDiscountAmount: n(receipt.weeklyDiscountAmount),
    monthlyDiscountAmount: n(receipt.monthlyDiscountAmount),
    cleaningFee: n(receipt.cleaningFee),
    extraGuestFee: n(receipt.extraGuestFee),
    petFee: n(receipt.petFee),
    guestServiceFee: n(receipt.guestServiceFee),
    guestServiceFeeRate: n(receipt.guestServiceFeeRate),
    occupancyTax: n(receipt.occupancyTax),
    totalPrice: n(receipt.totalPrice),
  };
}

/** One sentence for the refund preview (spec 016 §4.6). */
export function refundRuleText(rule: StayRefundRule): string {
  switch (rule) {
    case StayRefundRule.HostCancelled:
      return 'The host is cancelling, so the guest gets everything back.';
    case StayRefundRule.FullRefund:
      return 'You are inside the free cancellation window: everything comes back.';
    case StayRefundRule.GracePeriod:
      return 'You booked less than 48 hours ago and check-in is 14 or more days away: everything comes back.';
    case StayRefundRule.NightsKept:
      return 'The nights already used and one more night are not refunded.';
    case StayRefundRule.HalfRefund:
      return 'Half of the nightly price comes back, plus cleaning and pet fees.';
    case StayRefundRule.NoRefund:
      return 'The nights are not refunded. Cleaning and pet fees come back if the stay has not started.';
    case StayRefundRule.StayOver:
      return 'The stay is over.';
    default:
      return '';
  }
}

/** Guest count in words: "2 guests, 1 infant, 1 pet". */
export function guestCountsLabel(
  counts?: { adults: number; children: number; infants: number; pets: number } | null,
): string {
  if (!counts) return '';
  const guests = counts.adults + counts.children;
  const parts = [`${guests} guest${guests === 1 ? '' : 's'}`];
  if (counts.infants) parts.push(`${counts.infants} infant${counts.infants === 1 ? '' : 's'}`);
  if (counts.pets) parts.push(`${counts.pets} pet${counts.pets === 1 ? '' : 's'}`);
  return parts.join(', ');
}

/**
 * What the host earns on a stay: the nights after discounts, plus cleaning, extra
 * guests and pets, minus the host service fee. Guest fees and taxes are not the host's.
 */
export function hostPayout(receipt: StayReceiptLike & { hostServiceFee?: number | null }): {
  subtotal: number;
  hostServiceFee: number;
  payout: number;
} {
  const n = (v?: number | null) => Number(v ?? 0) || 0;
  const subtotal =
    n(receipt.nightlySubtotal) -
    n(receipt.weeklyDiscountAmount) -
    n(receipt.monthlyDiscountAmount) +
    n(receipt.cleaningFee) +
    n(receipt.extraGuestFee) +
    n(receipt.petFee);
  const fee = n(receipt.hostServiceFee);
  const round = (v: number) => Math.round(v * 100) / 100;
  return { subtotal: round(subtotal), hostServiceFee: round(fee), payout: round(subtotal - fee) };
}

/** One line for what a change costs (spec 016 B31, B32). */
export function changeMoneyText(change: {
  chargeAmount: number;
  refundAmount: number;
  keptAmount: number;
}): string {
  const money = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
  if (change.chargeAmount > 0) return `You pay ${money(change.chargeAmount)} now`;
  if (change.refundAmount > 0 && change.keptAmount > 0) {
    return `You get ${money(change.refundAmount)} back. ${money(change.keptAmount)} is not refunded under the cancellation policy`;
  }
  if (change.refundAmount > 0) return `You get ${money(change.refundAmount)} back`;
  if (change.keptAmount > 0) {
    return `The price goes down, but under the cancellation policy nothing is refunded (${money(change.keptAmount)})`;
  }
  return 'The price does not change';
}

/** The stay's dates as `yyyy-MM-dd` in the listing zone (what the change API takes). */
export function stayDayKeys(
  startAt: number,
  endAt: number,
  zone?: string | null,
): { checkIn: string; checkOut: string } {
  const key = (ms: number) =>
    DateTime.fromMillis(Number(ms), { zone: zone || 'UTC' }).toISODate() as string;
  return { checkIn: key(startAt), checkOut: key(endAt) };
}

/** What the total line means: charged, held for a request, or never charged. */
export function paidTotalLabel(status: BookingStatus, currency: string = 'USD'): string {
  switch (status) {
    case BookingStatus.Requested:
      return 'Total (held, not charged)';
    case BookingStatus.Pending:
      return `Total (${currency})`;
    case BookingStatus.DeclinedByHost:
    case BookingStatus.RequestExpired:
    case BookingStatus.RequestWithdrawn:
    case BookingStatus.CardDeclined:
      return 'Total (not charged)';
    default:
      return `Total paid (${currency})`;
  }
}
